import React, { useState, useEffect, useMemo } from 'react'
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography, 
  TextField,
  Alert,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material'
import AccessTime from '@mui/icons-material/AccessTime'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Warning from '@mui/icons-material/Warning'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getDataWithParams } from 'src/consumer/services/profileService'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { toast } from 'react-toastify'
import { formatTimeSlotInClinicTimezone } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/utils/nextVisitRosterCalendarUtils'

dayjs.extend(utc)

export default function CustomSlotHolder({ 
  onSlotSelect, 
  selectedDate, 
  mentorProfile,
  duration = 30, // duration in minutes
  clinicLocation = null // Clinic location for timezone
}) {
  const [selectedTime, setSelectedTime] = useState('')
  const [isChecking, setIsChecking] = useState(false)
  const [isAvailable, setIsAvailable] = useState(null) // null, true, false
  const [errorMessage, setErrorMessage] = useState('')
  const [checkedSlot, setCheckedSlot] = useState(null)

  // Set current time as default
  useEffect(() => {
    const now = dayjs()
    // Round to nearest 15 minutes for better UX
    const minutes = now.minute()
    const roundedMinutes = Math.ceil(minutes / 15) * 15
    const roundedTime = now.minute(roundedMinutes).second(0).millisecond(0)
    setSelectedTime(roundedTime.format('HH:mm'))
  }, [])

  // Create slot object from selected date and time
  const selectedSlot = useMemo(() => {
    if (!selectedDate || !selectedTime) return null
    
    const [hours, minutes] = selectedTime.split(':')
    
    // Get clinic timezone
    const clinicTimezone = clinicLocation?.schedules?.[0]?.timezone 
      ? (clinicLocation.schedules[0].timezone.includes('%') 
          ? decodeURIComponent(clinicLocation.schedules[0].timezone) 
          : clinicLocation.schedules[0].timezone)
      : clinicLocation?.timeZone?.offset
        ? (clinicLocation.timeZone.offset.includes('%')
            ? decodeURIComponent(clinicLocation.timeZone.offset)
            : clinicLocation.timeZone.offset)
        : '+05:30'
    
    // Create slot start time - interpret the selected time as being in clinic timezone
    // selectedDate is a dayjs object, we'll set the time on it
    const slotStartInClinicTz = selectedDate
      .clone()
      .hour(parseInt(hours))
      .minute(parseInt(minutes))
      .second(0)
      .millisecond(0)
      .utcOffset(clinicTimezone) // Set the timezone offset
    
    // Convert to UTC for API calls
    const slotStart = slotStartInClinicTz.utc()
    const slotEnd = slotStart.add(duration, 'minutes')
    
    // Format in clinic timezone for display
    const formattedTime = formatTimeSlotInClinicTimezone(
      slotStart, // Pass UTC, utility will convert to clinic timezone
      slotEnd,
      clinicLocation,
      duration
    )

    return {
      start: slotStart, // UTC for API
      end: slotEnd, // UTC for API
      label: formattedTime.label, // Formatted in clinic timezone
      labelWithTz: formattedTime.labelWithTz, // With timezone abbreviation
      time: formattedTime.startFormatted, // Start time in clinic timezone
      clinicLocation, // Store for later use
    }
  }, [selectedDate, selectedTime, duration, clinicLocation])

  // Check overlap with the API
  const checkAvailability = async () => {
    if (!selectedSlot || !mentorProfile?.id) {
      toast.error('Please select a time and ensure mentor data is loaded')
      return
    }

    setIsChecking(true)
    setIsAvailable(null)
    setErrorMessage('')

    try {
      const startTime = selectedSlot.start.toISOString()
      const endTime = selectedSlot.end.toISOString()
      
      const response = await getDataWithParams('/api/secure/mentor/slots/check-overlap', {
        'mentor-id': mentorProfile.id,
        'start-time': startTime,
        'end-time': endTime
      })

      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        setIsAvailable(true)
        setCheckedSlot(selectedSlot)
        toast.success('Time slot is available!')
      } else {
        setIsAvailable(false)
        setErrorMessage('You are not available on this slot')
        setCheckedSlot(null)
      }
    } catch (error) {
      console.error('Error checking slot availability:', error)
      setIsAvailable(false)
      setErrorMessage('Error checking availability. Please try again.')
      setCheckedSlot(null)
      toast.error('Failed to check slot availability')
    }

    setIsChecking(false)
  }

  // Handle continue action
  const handleContinue = () => {
    if (checkedSlot && onSlotSelect) {
      onSlotSelect(checkedSlot)
    }
  }

  // Reset availability check when time changes
  useEffect(() => {
    setIsAvailable(null)
    setCheckedSlot(null)
    setErrorMessage('')
  }, [selectedTime, selectedDate])

  return (
    <Box sx={{ p: 2 }}>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime color="primary" />
              <Typography variant="h6">
                Select a Custom Time
              </Typography>
            </Box>

            {/* Time Input */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select Time
              </Typography>
              <TextField
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 900, // 15 min intervals
                }}
                fullWidth
                sx={{ maxWidth: 200 }}
              />
              {selectedSlot && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Duration: {duration} minutes ({selectedSlot.labelWithTz || selectedSlot.label})
                </Typography>
              )}
            </Box>

            {/* Status Messages */}
            {isAvailable === false && (
              <Alert severity="error" icon={<Warning />}>
                {errorMessage || 'You are not available on this slot'}
              </Alert>
            )}

            {isAvailable === true && (
              <Alert severity="success" icon={<CheckCircle />}>
                Time slot is available and ready to book!
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={checkAvailability}
                disabled={!selectedSlot || isChecking}
                startIcon={isChecking ? <CircularProgress size={16} /> : null}
              >
                {isChecking ? 'Checking...' : 'Check Availability'}
              </Button>

              {isAvailable === true && (
                <Button
                  variant="contained"
                  onClick={handleContinue}
                  color="primary"
                >
                  Continue
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Help Text */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
        Select your preferred time and check availability before proceeding
      </Typography>
    </Box>
  )
}
